import React, { memo, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-xl shadow-lg border border-gray-100 text-xs">
        <p className="font-bold text-gray-800 mb-1.5">{`Ngày: ${label}`}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 py-0.5" style={{ color: entry.color }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="font-medium text-gray-700">{entry.name}:</span>
            <span className="font-bold">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

function TrendLineChartCard({ data, isLoading = false, timeLabel = '' }) {
  const chartData = useMemo(() => {
    if (Array.isArray(data) && data.length > 0) {
      return data.map((item) => ({
        ...item,
        hoanThanh: Number(item.hoanThanh ?? item.daGiaiQuyet ?? 0),
        quaHan: Number(item.quaHan ?? 0),
        tongPhanAnh: Number(item.tongPhanAnh ?? 0),
      }));
    }
    return [];
  }, [data]);

  const yAxisConfig = useMemo(() => {
    const maxVal = Math.max(
      ...(chartData.flatMap(d => [d.hoanThanh, d.quaHan, d.tongPhanAnh]).map(v => Number(v) || 0)),
      0
    );

    if (maxVal <= 0) {
      return { domain: [0, 5], ticks: [0, 1, 2, 3, 4, 5] };
    }
    if (maxVal <= 5) {
      const top = Math.max(maxVal + 1, 4);
      const ticks = Array.from({ length: top + 1 }, (_, i) => i);
      return { domain: [0, top], ticks };
    }
    if (maxVal <= 20) {
      const step = 5;
      const top = Math.ceil(maxVal / step) * step;
      const ticks = [];
      for (let i = 0; i <= top; i += step) ticks.push(i);
      return { domain: [0, top], ticks };
    }
    if (maxVal <= 50) {
      const step = 10;
      const top = Math.ceil(maxVal / step) * step;
      const ticks = [];
      for (let i = 0; i <= top; i += step) ticks.push(i);
      return { domain: [0, top], ticks };
    }

    const power = Math.pow(10, Math.floor(Math.log10(maxVal)));
    let step = power / 2;
    if (maxVal / step > 6) step = power;
    const top = Math.ceil(maxVal / step) * step;
    const ticks = [];
    for (let i = 0; i <= top; i += step) ticks.push(i);
    return { domain: [0, top], ticks };
  }, [chartData]);

  return (
    <div className="bg-white rounded-lg p-4 md:p-5 shadow-xs border border-gray-200 min-w-0 overflow-hidden">
      {/* Header */}
      <h3 className="text-xs md:text-sm font-bold text-gray-800 uppercase tracking-wider mb-3">
        Xu hướng phản ánh theo thời gian {timeLabel ? `(${timeLabel})` : ''}
      </h3>

      {/* Chart Canvas */}
      <div className="w-full min-w-0 h-[260px] flex flex-col justify-center">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center animate-pulse bg-gray-50/50 rounded-md">
            <span className="text-xs text-gray-400 font-semibold">Đang tải biểu đồ xu hướng...</span>
          </div>
        ) : chartData.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center rounded-md bg-gray-50/50 text-xs font-semibold text-gray-400">
            Chưa có dữ liệu xu hướng phản ánh
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={260}>
            <LineChart
              data={chartData}
              margin={{ top: 15, right: 20, left: -20, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={{ stroke: '#4b5563', strokeWidth: 1 }}
                tickLine={false}
                dy={5}
              />
              <YAxis
                ticks={yAxisConfig.ticks}
                domain={yAxisConfig.domain}
                allowDecimals={false}
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={{ stroke: '#4b5563', strokeWidth: 1 }}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '15px', fontSize: '12px' }}
                iconType="circle"
                iconSize={8}
              />
              <Line
                type="monotone"
                dataKey="tongPhanAnh"
                name="Tổng phản ánh"
                stroke="#3B82F6"
                strokeWidth={2.5}
                dot={{ fill: '#3B82F6', r: 4, strokeWidth: 1.5, stroke: '#ffffff' }}
                activeDot={{ r: 6, fill: '#2563EB', stroke: '#ffffff', strokeWidth: 2 }}
              />
              <Line
                type="monotone"
                dataKey="quaHan"
                name="Quá hạn"
                stroke="#EF4444"
                strokeWidth={2.5}
                strokeDasharray="5 5"
                dot={{ fill: '#EF4444', r: 4.5, strokeWidth: 2, stroke: '#ffffff' }}
                activeDot={{ r: 6.5, fill: '#DC2626', stroke: '#ffffff', strokeWidth: 2 }}
              />
              <Line
                type="monotone"
                dataKey="hoanThanh"
                name="Hoàn thành"
                stroke="#10B981"
                strokeWidth={2.5}
                dot={{ fill: '#10B981', r: 4, strokeWidth: 1.5, stroke: '#ffffff' }}
                activeDot={{ r: 6, fill: '#059669', stroke: '#ffffff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default memo(TrendLineChartCard);
